// Global Instructions Rule Applied!
//
// Agent draft card — an in-app suggestion an internal agent wrote for the
// responsible team member (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 0; Outlook export Phase 6a). Agent-written content only: kind Tag
// (blue = reference), subject, To/Cc chips, body through AgentMarkdown, muted
// rationale, serif-italic evidence citations (the signal-card treatment).
//
// Actions per the word-button grammar (docs/FRONTEND_DESIGN_PATTERNS.md):
//   proposed  Dismiss (danger, bordered) · Copy (default) · Send to Outlook
//             (primary, rightmost — only when the caller's mailbox is enabled)
//   exported  Tag "In Outlook" (blue) · Mark as sent (default) · Open in
//             Outlook (navigation, bordered); the sent-candidate line
//             ("Looks like you sent this…") carries Not this · Confirm
//   sent      Tag "Sent" (green) + the linked interaction
// Reservoir never sends: Send to Outlook creates the draft in the USER's own
// Outlook Drafts folder; the user edits and sends it there. Phase 6b learns
// the send three ways (exact by internetMessageId — no UI; fuzzy candidate
// the user confirms here; Mark as sent with a picker here).
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Select, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRobot } from '@fortawesome/free-solid-svg-icons'
import { AgentMarkdown } from '../../../core/components/AgentMarkdown.tsx'
import { CopyButton } from '../../../core/components/CopyButton.tsx'
import { AgentDraftInteractionRef, AgentDraftRecord } from '../../../state_manager/data_models/agent_drafts_model.ts'

// Status vocabulary (FRONTEND_DESIGN_PATTERNS "Colour & status"): draft /
// pending / reference = blue, done = green, lapsed = default.
// Exported so the section's COLLAPSED summary rows carry the same status
// vocabulary as the cards they fold away — one map, not two.
export const STATUS_COLOR: Record<string, string> = {
  proposed: 'blue',
  exported: 'blue',
  sent: 'green',
  accepted: 'green',
  dismissed: 'default',
  superseded: 'default',
  expired: 'default'
}
export const STATUS_LABEL: Record<string, string> = {
  proposed: 'proposed',
  exported: 'In Outlook',
  sent: 'Sent',
  accepted: 'accepted',
  dismissed: 'dismissed',
  superseded: 'superseded',
  expired: 'expired'
}

interface AgentDraftCardProps {
  draft: AgentDraftRecord
  busy?: boolean
  /** The card is on the account/prospect form — hide the account line. */
  hideAccount?: boolean
  /** Send to Outlook is on for the caller (global switch AND their mailbox). */
  outlookEnabled?: boolean
  onOpenAccount?: (accountId: string) => void
  /** Copy — records accepted/copied. */
  onAccept?: (draft: AgentDraftRecord, action: 'copied') => Promise<void> | void
  onDismiss?: (draft: AgentDraftRecord, reason: string | null) => Promise<void> | void
  /** Send to Outlook — the backend creates the draft in the caller's mailbox. */
  onExportOutlook?: (draft: AgentDraftRecord) => Promise<void> | void
  /** Confirm (true) / Not this (false) on the fuzzy sent candidate. */
  onConfirmCandidate?: (draft: AgentDraftRecord, accept: boolean) => Promise<void> | void
  /** Mark as sent with an email of the account (manual tier). */
  onMarkSent?: (draft: AgentDraftRecord, interactionId: string) => Promise<void> | void
  /** The Mark as sent picker's options — the account's recent interactions. */
  loadInteractions?: (draft: AgentDraftRecord) => Promise<AgentDraftInteractionRef[]>
  onOpenInteraction?: (interactionId: string) => void
}

const fmt = (iso: string | null | undefined): string => (iso ? dayjs(iso).format('MM/DD/YYYY') : '')

export const AgentDraftCard: React.FC<AgentDraftCardProps> = ({
  draft, busy = false, hideAccount = false, outlookEnabled = false,
  onOpenAccount, onAccept, onDismiss, onExportOutlook, onConfirmCandidate, onMarkSent, loadInteractions, onOpenInteraction
}) => {
  const [dismissOpen, setDismissOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [markSentOpen, setMarkSentOpen] = useState(false)
  const [options, setOptions] = useState<AgentDraftInteractionRef[] | null>(null)
  const [optionsError, setOptionsError] = useState<string | null>(null)
  const [pickedId, setPickedId] = useState<string | null>(null)
  const status = draft.agent_drafts__status
  const isOpen = status === 'proposed'
  const isExported = status === 'exported'
  const isSent = status === 'sent'
  const plainText = useMemo(
    () => `${draft.agent_drafts__subject ? `Subject: ${draft.agent_drafts__subject}\n\n` : ''}${draft.agent_drafts__body_markdown}`,
    [draft.agent_drafts__subject, draft.agent_drafts__body_markdown]
  )
  const people = (list: AgentDraftRecord['prog__suggested_to']) =>
    list.map((c) => (
      <Tooltip key={c.contacts__id} title={c.contacts__email ?? ''}>
        <Tag>{c.contacts__name ?? c.contacts__email ?? 'contact'}</Tag>
      </Tooltip>
    ))
  const webLink = draft.agent_drafts__outlook_web_link ?? null
  const sibling = draft.prog__exported_sibling ?? null
  const candidate = isExported ? draft.prog__candidate_interaction ?? null : null

  // The picker loads when the modal opens (not on render — most cards never
  // need it) and the pre-selection is the candidate when there is one.
  useEffect(() => {
    if (!markSentOpen || options !== null || !loadInteractions) return
    let cancelled = false
    loadInteractions(draft)
      .then((rows) => { if (!cancelled) { setOptions(rows); setOptionsError(null) } })
      .catch((e) => { if (!cancelled) { setOptions([]); setOptionsError(e instanceof Error ? e.message : 'Failed to load emails') } })
    return () => { cancelled = true }
  }, [markSentOpen, options, loadInteractions, draft])
  const openMarkSent = () => { setPickedId(candidate?.interactions__id ?? null); setMarkSentOpen(true) }
  const interactionLabel = (i: AgentDraftInteractionRef): string =>
    `${fmt(i.interactions__datetime) || '—'} · ${i.interactions__subject ?? '(no subject)'}${i.interactions__to_names ? ` · to ${i.interactions__to_names}` : ''}`

  const footerNote = (): string => {
    const who = draft.prog__assigned_to_name ? `For ${draft.prog__assigned_to_name}` : 'Unassigned'
    if (isExported) return `${who} · sent to Outlook by ${draft.prog__exported_by_name ?? 'you'} on ${fmt(draft.agent_drafts__exported_at)}`
    if (isSent) return `${who} · sent ${fmt(draft.agent_drafts__sent_at)}`
    if (!isOpen && draft.prog__reviewed_by_name) return `${who} · ${status} by ${draft.prog__reviewed_by_name}`
    return who
  }

  return (
    <article data-component='AgentDraftCard' data-status={status} className='bg-white rounded-lg border border-gray-100 px-4 py-3' style={{ borderLeftWidth: 4, borderLeftColor: '#3E6FA8' }}>
      <div className='flex items-center gap-2 flex-wrap'>
        <Tag color='blue' icon={<FontAwesomeIcon icon={faRobot} />}>{draft.agent_draft_kinds__label}</Tag>
        <Tag color={STATUS_COLOR[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Tag>
        {draft.agent_drafts__outlook_is_reply ? <Tag>reply</Tag> : null}
        <span className='text-gray-500 text-xs'>{draft.internal_agent_types__name}</span>
        <span className='ml-auto text-gray-400 text-xs'>{fmt(draft.agent_drafts__date_created)}</span>
      </div>
      {!hideAccount ? (
        <div className='mt-1 text-sm'>
          {onOpenAccount ? (
            <button type='button' className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left' onClick={() => onOpenAccount(draft.agent_drafts__account_id)}>
              {draft.accounts__name}
            </button>
          ) : (
            <span>{draft.accounts__name}</span>
          )}
          {draft.funds__name ? <span className='text-gray-500'> · {draft.funds__name}</span> : null}
        </div>
      ) : draft.funds__name ? (
        <div className='mt-1 text-xs text-gray-500'>{draft.prospects__name ?? draft.funds__name}</div>
      ) : null}
      {draft.agent_drafts__subject ? <div className='mt-2 font-semibold text-sm'>{draft.agent_drafts__subject}</div> : null}
      {draft.prog__suggested_to.length || draft.prog__suggested_cc.length ? (
        <div className='mt-1 text-xs text-gray-600 flex flex-wrap items-center gap-1'>
          {draft.prog__suggested_to.length ? <><span>To:</span>{people(draft.prog__suggested_to)}</> : null}
          {draft.prog__suggested_cc.length ? <><span className='ml-2'>Cc:</span>{people(draft.prog__suggested_cc)}</> : null}
        </div>
      ) : null}
      <div className='mt-2'>
        <AgentMarkdown source={draft.agent_drafts__body_markdown} />
      </div>
      {draft.agent_drafts__rationale ? <div className='mt-2 text-xs text-gray-500'>Why: {draft.agent_drafts__rationale}</div> : null}
      {draft.prog__evidence.length ? (
        <div className='mt-2 flex flex-col gap-1'>
          {draft.prog__evidence.slice(0, 3).map((e, i) => (
            <blockquote key={i} className='m-0 pl-3 border-l-2 border-gray-200 text-xs text-gray-600' style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              {e.excerpt || e.label || e.kind}
              {e.label && e.excerpt ? <span className='not-italic text-gray-400'> — {e.kind} · {e.label}{e.occurred_at ? ` · ${fmt(e.occurred_at)}` : ''}</span> : null}
            </blockquote>
          ))}
        </div>
      ) : null}

      {isOpen && sibling ? (
        <div data-testid='exported-sibling' className='mt-2 text-xs text-amber-700'>
          An unsent Outlook draft for this already exists (sent to Outlook {fmt(sibling.agent_drafts__exported_at)}).
          {sibling.agent_drafts__outlook_web_link ? (
            <> <a href={sibling.agent_drafts__outlook_web_link} target='_blank' rel='noopener noreferrer' className='text-blue-600 hover:underline'>Open it</a> before creating another.</>
          ) : null}
        </div>
      ) : null}
      {candidate ? (
        <div data-testid='sent-candidate' className='mt-2 text-xs text-amber-700 flex items-center gap-2 flex-wrap'>
          <span>
            Looks like you sent this on {fmt(candidate.interactions__datetime)}
            {candidate.interactions__to_names ? ` to ${candidate.interactions__to_names}` : ''}
            {candidate.interactions__subject ? ` (“${candidate.interactions__subject}”)` : ''}.
          </span>
          {onConfirmCandidate ? (
            <span className='inline-flex items-center gap-1'>
              <Button size='small' loading={busy} onClick={() => onConfirmCandidate(draft, false)}>Not this</Button>
              <Button size='small' type='primary' loading={busy} onClick={() => onConfirmCandidate(draft, true)}>Confirm</Button>
            </span>
          ) : null}
        </div>
      ) : null}
      {isExported && !candidate && draft.agent_drafts__outlook_sent_at ? (
        <div data-testid='outlook-sent' className='mt-2 text-xs text-gray-600'>
          Outlook reports this was sent on {fmt(draft.agent_drafts__outlook_sent_at)}, but the email has not reached Reservoir yet. It links automatically when it does — or use Mark as sent.
        </div>
      ) : null}
      {isExported && draft.agent_drafts__outlook_item_missing_at ? (
        <div data-testid='item-missing' className='mt-2 text-xs text-gray-500'>
          Outlook draft no longer found (checked {fmt(draft.agent_drafts__outlook_item_missing_at)}). If you sent the text another way, use Mark as sent; if you dropped it, nothing else is needed.
        </div>
      ) : null}
      {isSent && draft.prog__sent_interaction ? (
        <div className='mt-2 text-xs text-gray-600'>
          Sent as{' '}
          {onOpenInteraction ? (
            <button type='button' className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0' onClick={() => onOpenInteraction(draft.prog__sent_interaction!.interactions__id)}>
              {draft.prog__sent_interaction.interactions__subject ?? 'the email'}
            </button>
          ) : (
            <span>{draft.prog__sent_interaction.interactions__subject ?? 'the email'}</span>
          )}
          {draft.prog__sent_interaction.interactions__datetime ? ` · ${fmt(draft.prog__sent_interaction.interactions__datetime)}` : ''}
        </div>
      ) : null}

      <div className='mt-3 flex items-center gap-2 flex-wrap'>
        <span className='text-xs text-gray-400 mr-auto'>{footerNote()}</span>
        {isOpen && onDismiss ? (
          <Button danger size='small' loading={busy} onClick={() => setDismissOpen(true)}>Dismiss</Button>
        ) : null}
        {isOpen && onAccept ? (
          <span onClick={() => onAccept(draft, 'copied')}>
            <CopyButton value={plainText} label='Copy' successMessage='Draft copied' />
          </span>
        ) : null}
        {isOpen && outlookEnabled && onExportOutlook ? (
          <Button type='primary' size='small' loading={busy} onClick={() => onExportOutlook(draft)}>Send to Outlook</Button>
        ) : null}
        {isExported && onMarkSent ? (
          <Button size='small' loading={busy} onClick={openMarkSent}>Mark as sent</Button>
        ) : null}
        {isExported && webLink ? (
          <Button size='small' onClick={() => window.open(webLink, '_blank', 'noopener,noreferrer')}>Open in Outlook</Button>
        ) : null}
      </div>

      <Modal title='Mark as sent' open={markSentOpen} okText='Mark as sent'
        okButtonProps={{ disabled: !pickedId, loading: busy }}
        onOk={async () => { if (pickedId) { await onMarkSent?.(draft, pickedId); setMarkSentOpen(false) } }}
        onCancel={() => setMarkSentOpen(false)} destroyOnClose>
        <div className='text-xs text-gray-600 mb-1'>
          Pick the email you sent for this draft. Only emails already in Reservoir on {draft.accounts__name} are listed — a send from Outlook usually arrives within a couple of minutes.
        </div>
        <Select
          data-testid='mark-sent-picker'
          style={{ width: '100%' }}
          showSearch
          optionFilterProp='label'
          placeholder={options === null ? 'Loading emails…' : 'Select an email'}
          loading={options === null}
          value={pickedId ?? undefined}
          onChange={(v: string) => setPickedId(v)}
          options={(options ?? []).map((i) => ({ value: i.interactions__id, label: interactionLabel(i) }))}
          notFoundContent={optionsError ?? (options !== null && options.length === 0 ? 'No emails on this account yet' : undefined)}
        />
      </Modal>

      <Modal title='Dismiss draft' open={dismissOpen} okText='Dismiss' okButtonProps={{ danger: true }}
        onOk={async () => { await onDismiss?.(draft, reason.trim() || null); setDismissOpen(false); setReason('') }}
        onCancel={() => setDismissOpen(false)} destroyOnClose>
        <div className='text-xs text-gray-600 mb-1'>Reason (optional — helps the agent learn what not to suggest)</div>
        <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
      </Modal>
    </article>
  )
}

export default AgentDraftCard
